<script setup lang="ts">
import { computed, watchEffect } from 'vue';
import { DgrIcon } from '@stockmarkteam/donguri-ui';
import TextCounter from '@/components/common/molecules/text-counter.vue';
import AdditionalBusinessContextSuggestion from '@/components/settings/additional-business-context-suggestion.vue';
import type {
  AdditionalBusinessContextSuggestionRefreshFrom,
  PersonalSurveyProfileInput,
} from '@/types';
import { useAdditionalBusinessContextSuggestion } from '@/utils/composables/useAdditionalBusinessContextSuggestion';
import { appendSuggestion } from '@/utils/personalSurveyProfile';
import { featureFlags } from '@/featureFlags';

const props = withDefaults(
  defineProps<{
    required?: boolean;
    withAdditionalBusinessContext?: boolean;
    withAdditionalBusinessContextSuggestion?: boolean;
  }>(),
  {
    required: false,
    withAdditionalBusinessContext: true,
    withAdditionalBusinessContextSuggestion: false,
  },
);

const emit = defineEmits<{
  suggestionApply: [];
  suggestionRefresh: [from: AdditionalBusinessContextSuggestionRefreshFrom];
}>();

const model = defineModel<PersonalSurveyProfileInput>({
  default: () => ({
    department: '',
    market_area: '',
    technology_area: '',
    technical_challenge: '',
  }),
});
const valid = defineModel<boolean>('valid');

const MAX_DEPARTMENT_NAME_LENGTH = 50;
const MAX_MARKET_AREA_LENGTH = 50;
const MAX_TECHNOLOGY_AREA_LENGTH = 50;
const MAX_TECHNICAL_CHALLENGE_LENGTH = 50;
const MAX_ADDITIONAL_BUSINESS_CONTEXT_LENGTH = 500;

const field = (key: keyof PersonalSurveyProfileInput) =>
  computed({
    get: () => model.value[key] ?? '',
    set: value => (model.value = { ...model.value, [key]: value }),
  });

const department = field('department');
const marketArea = field('market_area');
const technologyArea = field('technology_area');
const technicalChallenge = field('technical_challenge');
const additionalBusinessContext = field('additional_business_context');

const showsAdditionalBusinessContext = computed(
  () =>
    featureFlags.ANDEV_10700_INSIGHT_ADDITIONAL_BUSINESS_CONTEXT &&
    props.withAdditionalBusinessContext,
);
const showsSuggestion = computed(
  () =>
    showsAdditionalBusinessContext.value &&
    featureFlags.ANDEV_10813_INSIGHT_ADDITIONAL_BUSINESS_CONTEXT_SUGGESTION &&
    props.withAdditionalBusinessContextSuggestion,
);
const ADDITIONAL_BUSINESS_CONTEXT_PLACEHOLDER = [
  '例１：新規事業の種を探しています。',
  '例２：自社の強みである振動センサーの技術を活かせる用途を知りたい。',
  '例３：技術だけでなく、経営判断に使える情報もほしい。',
].join('\n');

const suggestion = useAdditionalBusinessContextSuggestion(model);
const addSuggestion = (text: string) => {
  additionalBusinessContext.value = appendSuggestion(
    additionalBusinessContext.value,
    text,
  );
  emit('suggestionApply');
};
const refreshSuggestion = () => {
  emit(
    'suggestionRefresh',
    suggestion.status.value === 'error' ? 'generation_error' : 'suggestions',
  );
  suggestion.generate();
};

const isDepartmentTooLong = computed(
  () => department.value.length > MAX_DEPARTMENT_NAME_LENGTH,
);
const isMarketAreaTooLong = computed(
  () => marketArea.value.length > MAX_MARKET_AREA_LENGTH,
);
const isTechnologyAreaTooLong = computed(
  () => technologyArea.value.length > MAX_TECHNOLOGY_AREA_LENGTH,
);
const isTechnicalChallengeTooLong = computed(
  () => technicalChallenge.value.length > MAX_TECHNICAL_CHALLENGE_LENGTH,
);
const isAdditionalBusinessContextTooLong = computed(
  () =>
    showsAdditionalBusinessContext.value &&
    additionalBusinessContext.value.length >
      MAX_ADDITIONAL_BUSINESS_CONTEXT_LENGTH,
);

const withinLength = computed(
  () =>
    !isDepartmentTooLong.value &&
    !isMarketAreaTooLong.value &&
    !isTechnologyAreaTooLong.value &&
    !isTechnicalChallengeTooLong.value &&
    !isAdditionalBusinessContextTooLong.value,
);
const allFilled = computed(
  () =>
    !!department.value &&
    !!marketArea.value &&
    !!technologyArea.value &&
    !!technicalChallenge.value,
);

watchEffect(() => {
  valid.value = withinLength.value && (!props.required || allFilled.value);
});
</script>

<template>
  <div class="c-formBlock">
    <div class="c-formBlock__label">
      所属部門/部門の役割<template v-if="required">（必須）</template>
      <VTooltip placement="right">
        <DgrIcon name="question-circle" size="small" />
        <template #popper>
          入力例
          <ul class="tooltip-list">
            <li>研究開発部</li>
            <li>経営企画部</li>
            <li>事業部/製品企画部</li>
            <li>新規事業開発部</li>
            <li>DX推進部</li>
          </ul>
        </template>
      </VTooltip>
    </div>
    <input
      class="c-textInput"
      :class="{ 'c-formInput--error': isDepartmentTooLong }"
      type="text"
      v-model.trim="department"
      placeholder="例：研究開発部"
    />
    <TextCounter :max-length="MAX_DEPARTMENT_NAME_LENGTH" :text="department" />
  </div>
  <div class="c-formBlock">
    <div class="c-formBlock__label">
      対象市場/対象用途<template v-if="required">（必須）</template>
      <VTooltip placement="right">
        <DgrIcon name="question-circle" size="small" />
        <template #popper>
          入力例
          <ul class="tooltip-list">
            <li>
              研究開発部
              <ul class="tooltip-list">
                <li>EV用インバーター</li>
              </ul>
            </li>
            <li>
              経営企画部
              <ul class="tooltip-list">
                <li>欧州市場向けバイオ医薬品</li>
              </ul>
            </li>
            <li>
              事業部/製品企画部
              <ul class="tooltip-list">
                <li>建材一体型太陽電池（BIPV）</li>
              </ul>
            </li>
            <li>
              新規事業開発部
              <ul class="tooltip-list">
                <li>メディカル領域における生体内インプラント市場</li>
              </ul>
            </li>
            <li>
              DX推進部
              <ul class="tooltip-list">
                <li>研究開発業務におけるDX推進</li>
              </ul>
            </li>
          </ul>
        </template>
      </VTooltip>
    </div>
    <input
      class="c-textInput"
      :class="{ 'c-formInput--error': isMarketAreaTooLong }"
      type="text"
      v-model.trim="marketArea"
      placeholder="例：EV用インバーター"
    />
    <TextCounter :max-length="MAX_MARKET_AREA_LENGTH" :text="marketArea" />
  </div>
  <div class="c-formBlock">
    <div class="c-formBlock__label">
      技術領域/業務領域<template v-if="required">（必須）</template>
      <VTooltip placement="right">
        <DgrIcon name="question-circle" size="small" />
        <template #popper>
          入力例
          <ul class="tooltip-list">
            <li>
              研究開発部
              <ul class="tooltip-list">
                <li>パワー半導体</li>
              </ul>
            </li>
            <li>
              経営企画部
              <ul class="tooltip-list">
                <li>バイオ医薬品市場動向調査、規制調査</li>
              </ul>
            </li>
            <li>
              事業部/製品企画部
              <ul class="tooltip-list">
                <li>ペロブスカイト太陽電池モジュール設計</li>
              </ul>
            </li>
            <li>
              新規事業開発部
              <ul class="tooltip-list">
                <li>皮膜コーティング技術</li>
              </ul>
            </li>
            <li>
              DX推進部
              <ul class="tooltip-list">
                <li>生成AI</li>
              </ul>
            </li>
          </ul>
        </template>
      </VTooltip>
    </div>
    <input
      class="c-textInput"
      :class="{ 'c-formInput--error': isTechnologyAreaTooLong }"
      type="text"
      v-model.trim="technologyArea"
      placeholder="例：パワー半導体"
    />
    <TextCounter
      :max-length="MAX_TECHNOLOGY_AREA_LENGTH"
      :text="technologyArea"
    />
  </div>
  <div class="c-formBlock">
    <div class="c-formBlock__label">
      技術課題/業務課題<template v-if="required">（必須）</template>
      <VTooltip placement="right">
        <DgrIcon name="question-circle" size="small" />
        <template #popper>
          入力例
          <ul class="tooltip-list">
            <li>
              研究開発部
              <ul class="tooltip-list">
                <li>SiCインバーターの効率化と熱暴走対策</li>
              </ul>
            </li>
            <li>
              経営企画部
              <ul class="tooltip-list">
                <li>欧州規制への対応、競合との差別化、製造コストの最適化</li>
              </ul>
            </li>
            <li>
              事業部/製品企画部
              <ul class="tooltip-list">
                <li>屋外耐候性確保と発電効率の安定化</li>
              </ul>
            </li>
            <li>
              新規事業開発部
              <ul class="tooltip-list">
                <li>
                  生体内インプラントに求められるコーティング材の物性要件が不明
                </li>
              </ul>
            </li>
            <li>
              DX推進部
              <ul class="tooltip-list">
                <li>業務効率化の推進</li>
              </ul>
            </li>
          </ul>
        </template>
      </VTooltip>
    </div>
    <input
      class="c-textInput"
      :class="{ 'c-formInput--error': isTechnicalChallengeTooLong }"
      type="text"
      v-model.trim="technicalChallenge"
      placeholder="例：SiCインバーターの効率化と熱暴走対策"
    />
    <TextCounter
      :max-length="MAX_TECHNICAL_CHALLENGE_LENGTH"
      :text="technicalChallenge"
    />
  </div>
  <div v-if="showsAdditionalBusinessContext" class="c-formBlock">
    <div class="c-formBlock__label">業務の補足</div>
    <p class="c-text-regular--s description">
      上の項目で書ききれない業務の目的や自社技術、知りたいことを自由に書いてください。
    </p>
    <textarea
      class="c-textArea additional-business-context"
      :class="{ 'c-formInput--error': isAdditionalBusinessContextTooLong }"
      v-model.trim="additionalBusinessContext"
      :placeholder="
        showsSuggestion ? ADDITIONAL_BUSINESS_CONTEXT_PLACEHOLDER : undefined
      "
      @focus="suggestion.generateIfStale"
    ></textarea>
    <TextCounter
      :max-length="MAX_ADDITIONAL_BUSINESS_CONTEXT_LENGTH"
      :text="additionalBusinessContext"
    />
    <AdditionalBusinessContextSuggestion
      v-if="showsSuggestion"
      class="suggestion-box"
      :status="suggestion.status.value"
      :suggestions="suggestion.suggestions.value"
      :can-regenerate="suggestion.canRegenerate.value"
      @add="addSuggestion"
      @regenerate="refreshSuggestion"
    />
    <section
      v-else
      class="examples c-text-regular--s"
      aria-label="業務の補足の入力例"
    >
      <div class="c-title-bold--s">業務の補足の入力例</div>
      <div>例１： 新規事業の種を探しています。</div>
      <div>
        例２： 自社の強みである振動センサーの技術を活かせる用途を知りたい。
      </div>
      <div>例３： 技術だけでなく、経営判断に使える情報もほしい。</div>
    </section>
  </div>
</template>

<style lang="scss" scoped>
.c-formBlock {
  margin-bottom: 12px;

  .c-formBlock__label {
    align-items: center;
    gap: 4px;
  }

  input,
  textarea {
    outline: none;
  }

  .description {
    margin: 0 0 4px;
    color: $color-gray600;
  }

  .additional-business-context {
    width: 100%;
    height: 120px;
    resize: vertical;

    // aseries.css は input::placeholder にしか色を付けていない。
    &::placeholder {
      color: $color-gray600;
    }
  }

  .suggestion-box {
    margin-top: 8px;
  }

  .examples {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-top: 8px;
    padding: 12px 16px;
    border-radius: 4px;
    background: $color-gray200;
    color: $color-gray800;
  }
}

.tooltip-list {
  padding-left: 12px;
  list-style-type: disc;
}
</style>
